import { useRef, useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import {
  Box,
  Avatar,
  Stack,
  Tooltip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
} from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";

import { getCroppedImg } from "./helper";

const ImageUploader = ({ defaultImage, onUpload }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [open, setOpen] = useState(false);
  const fileInputRef = useRef(null);

  const readFile = async (file) => {
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);

      reader.readAsDataURL(file);
    });
  };

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageDataUrl = await readFile(file);
      setImageSrc(imageDataUrl);
      setOpen(true);
    }
  };

  const handleCrop = async () => {
    try {
      const { blob, previewUrl } = await getCroppedImg(
        imageSrc,
        croppedAreaPixels,
      );
      setCroppedImage(previewUrl);

      const file = new File([blob], "cropped.jpg", { type: "image/jpeg" });

      if (onUpload) onUpload(file);
    } catch (err) {
      console.error(err);
    }

    setOpen(false);
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box>
      <Stack spacing={2} alignItems="center">
        <Box width="100px" height="100px" position="relative">
          <Tooltip title="Click to upload">
            <Avatar
              src={croppedImage || defaultImage}
              onClick={handleAvatarClick}
              sx={{
                width: 100,
                height: 100,
                cursor: "pointer",
                borderRadius: "8px",
                border: "2px solid #009a80",
              }}
            />
          </Tooltip>
          <PhotoCameraIcon
            color="action"
            sx={{
              position: "absolute",
              right: 0,
              bottom: "-2px",
              pointerEvents: "none",
            }}
          />
        </Box>

        <input
          ref={fileInputRef}
          hidden
          accept="image/*"
          type="file"
          onChange={handleFileChange}
        />
      </Stack>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Crop Image</DialogTitle>
        <DialogContent>
          <div style={{ position: "relative", width: "100%", height: 400 }}>
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(e, zoom) => setZoom(zoom)}
            style={{ marginTop: 16 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleCrop} variant="contained">
            Crop
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImageUploader;
